/**
 * ColorPicker — color swatch + hex text input row.
 * Single responsibility: let the user pick or type a CSS color value.
 */

import React, { useRef, useState } from 'react';

export interface ColorPickerProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  isDark: boolean;
}

/** Test whether a string is a valid CSS color using a throwaway element. */
function isValidCSSColor(s: string): boolean {
  if (!s.trim()) return false;
  const el = document.createElement('div');
  el.style.color = s;
  return el.style.color !== '';
}

/**
 * ColorPicker component — swatch + hex input + clear button.
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({ label, value, onChange, isDark }) => {
  const lastValidRef = useRef<string>(value ?? '');
  const [textVal, setTextVal] = useState<string>(value ?? '');

  // Keep textVal in sync when value changes externally
  React.useEffect(() => {
    setTextVal(value ?? '');
    if (value) lastValidRef.current = value;
  }, [value]);

  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
    flex: 1,
    whiteSpace: 'nowrap',
  };

  const swatchStyle: React.CSSProperties = {
    width: 32,
    height: 28,
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    cursor: 'pointer',
    padding: 2,
    flexShrink: 0,
    background: 'none',
  };

  const textInputStyle: React.CSSProperties = {
    width: 80,
    padding: '4px 6px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 11,
    fontFamily: 'monospace',
    boxSizing: 'border-box',
  };

  const clearBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    padding: '0 4px',
    fontSize: 14,
    lineHeight: 1,
    flexShrink: 0,
  };

  const handleSwatchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    lastValidRef.current = v;
    setTextVal(v);
    onChange(v);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTextVal(e.target.value);
  };

  const handleTextBlur = () => {
    const trimmed = textVal.trim();
    if (trimmed === '') {
      onChange(undefined);
      lastValidRef.current = '';
    } else if (isValidCSSColor(trimmed)) {
      lastValidRef.current = trimmed;
      onChange(trimmed);
    } else {
      // Revert to last valid
      setTextVal(lastValidRef.current);
    }
  };

  const handleClear = () => {
    lastValidRef.current = '';
    setTextVal('');
    onChange(undefined);
  };

  const swatchColor = value || (isDark ? '#0f172a' : '#ffffff');

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <input
        type="color"
        value={swatchColor.startsWith('#') ? swatchColor : '#000000'}
        onChange={handleSwatchChange}
        style={swatchStyle}
        aria-label={`${label} color swatch`}
        title={label}
      />
      <input
        type="text"
        value={textVal}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        placeholder="—"
        style={textInputStyle}
        aria-label={`${label} hex color value`}
        spellCheck={false}
      />
      <button
        type="button"
        style={clearBtnStyle}
        onClick={handleClear}
        aria-label={`Clear ${label} color`}
        title="Clear"
      >
        ×
      </button>
    </div>
  );
};

ColorPicker.displayName = 'ColorPicker';
